import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShoppingCart, ChevronDown, X } from 'lucide-react';

const MINIMIZED_STORAGE_KEY = 'floatingCartMinimized';
const DISMISSED_COUNT_KEY = 'floatingCartDismissedAtCount';

const FloatingCart = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const { formatPrice } = useCurrency();
  
  // Initialize minimized state from localStorage
  const [userMinimized, setUserMinimized] = useState(() => {
    try {
      const stored = localStorage.getItem(MINIMIZED_STORAGE_KEY);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Track dismissed count in state for reactivity
  const [dismissedAtCount, setDismissedAtCount] = useState(() => {
    try {
      return parseInt(localStorage.getItem(DISMISSED_COUNT_KEY) || '-1', 10);
    } catch {
      return -1;
    }
  });

  // Cart is considered dismissed only if current count is <= the count when it was dismissed
  const isDismissed = dismissedAtCount >= 0 && cart.length <= dismissedAtCount;

  const navigateToCart = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/cart');
  }, [navigate]);

  const handleMinimize = useCallback(() => {
    setUserMinimized(true);
    try {
      localStorage.setItem(MINIMIZED_STORAGE_KEY, 'true');
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const handleExpand = useCallback(() => {
    setUserMinimized(false);
    try {
      localStorage.setItem(MINIMIZED_STORAGE_KEY, 'false');
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const handleDismiss = useCallback((e) => {
    e.stopPropagation();
    const currentCount = cart.length;
    setDismissedAtCount(currentCount);
    try {
      localStorage.setItem(DISMISSED_COUNT_KEY, currentCount.toString());
    } catch {
      // Ignore localStorage errors
    }
  }, [cart.length]);

  // Don't render if cart is empty or dismissed
  if (cart.length === 0 || isDismissed) {
    return null;
  }

  // Show minimized view if user explicitly minimized it
  if (userMinimized) {
    return (
      <div className="fixed bottom-20 right-6 z-50">
        <button
          onClick={handleExpand}
          className="bg-[#0007b5] text-white rounded-full p-4 shadow-lg hover:bg-[#000690] transition-all flex items-center justify-center"
          aria-label="Open cart"
        >
          <ShoppingCart className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {cart.length}
          </span>
        </button>
        <button
          onClick={handleDismiss}
          className="absolute -top-2 -left-2 bg-slate-600 hover:bg-slate-700 text-white rounded-full p-1 shadow-md transition-colors"
          aria-label="Dismiss cart"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Expanded view - full cart summary (default when items in cart)
  return (
    <div 
      className="fixed bottom-20 right-6 bg-white border border-slate-200 rounded-xl shadow-lg p-4 max-w-sm z-50"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-800">Shopping Cart</h4>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{cart.length} {cart.length === 1 ? 'item' : 'items'}</Badge>
          <button
            onClick={handleMinimize}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
            aria-label="Minimize cart"
          >
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
            aria-label="Dismiss cart"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>
      
      <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
        {cart.map(item => (
          <div key={item.id} className="flex justify-between text-sm">
            <span className="truncate flex-1">{item.name}</span>
            <span className="font-medium ml-2">{item.quantity}x</span>
          </div>
        ))}
      </div>
      
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold">Total:</span>
        <span className="font-bold text-lg">
          {formatPrice(cart.reduce((total, item) => total + (item.price * item.quantity), 0))}
        </span>
      </div>
      
      <Button 
        className="w-full bg-[#0007b5] hover:bg-[#000690] text-white"
        onClick={navigateToCart}
      >
        Open Cart ({cart.length})
      </Button>
    </div>
  );
};

export default FloatingCart;
